import { Pipe, PipeTransform } from '@angular/core';
import {
    bbox,
    booleanIntersects,
    buffer,
    cleanCoords,
    clustersDbscan,
    convex,
    featureCollection,
    point,
    polygon,
    simplify,
    union,
} from '@turf/turf';
import { Feature, Point, Polygon } from 'geojson';
import { Workarea } from '../workarea.class';

type StatusLike = {
    id: number | string;
    type?: string | null;
    colorcode?: string | null;
    name?: string | null;
};

type OutputPoly = {
    paths: google.maps.LatLngLiteral[];
    color: string;
    options: google.maps.PolygonOptions;
};

type ZoomConfig = {
    /** Upper bound of zoom levels that map to this bucket (inclusive) */
    maxZoom: number;
    clusteringRadiusKm: number;
    simplifyTolerance: number;
    bufferKm: number;
    pointBufferKm: number;
};

@Pipe({
    name: 'clusterWorkareas',
    standalone: false,
    pure: true,
})
export class WorkareaClusterPipe implements PipeTransform {
    /**
     * Per-bucket zoom configuration.
     * Key   = the normalised bucket value (used in cache keys).
     * Value = full config for that bucket.
     * Buckets are evaluated in ascending key order; the first bucket whose
     * maxZoom >= the actual zoom wins.
     */
    private readonly zoomConfig = new Map<number, ZoomConfig>([
        [8, { maxZoom: 10, clusteringRadiusKm: 1.5, simplifyTolerance: 0.001, bufferKm: 2, pointBufferKm: 1.0 }],
        [11, { maxZoom: 13, clusteringRadiusKm: 1.1, simplifyTolerance: 0.0004, bufferKm: 1, pointBufferKm: 0.75 }],
        [
            14,
            {
                maxZoom: Infinity,
                clusteringRadiusKm: 0.7,
                simplifyTolerance: 0.00012,
                bufferKm: 0.3,
                pointBufferKm: 0.25,
            },
        ],
    ]);

    private getZoomBucket(zoom: number): number {
        for (const [bucket, cfg] of this.zoomConfig) {
            if (zoom <= cfg.maxZoom) return bucket;
        }
        // fallback: last bucket
        return [...this.zoomConfig.keys()].at(-1)!;
    }

    private readonly cache = new Map<string, { value: OutputPoly[] }>();
    private readonly cacheMaxEntries = 30;

    transform(workareas: Workarea[], zoom: number, statuses?: StatusLike[] | null): OutputPoly[] {
        if (!workareas?.length) return [];

        const bucketZoom = this.getZoomBucket(zoom);
        const cfg = this.zoomConfig.get(bucketZoom)!;

        const cacheKey = this.buildCacheKey(workareas, bucketZoom, statuses);
        const cached = this.cache.get(cacheKey);
        if (cached) {
            return cached.value;
        }

        const visibleWorkareas = workareas;

        const { clusteringRadiusKm, simplifyTolerance, bufferKm, pointBufferKm } = cfg;

        const statusIdToMeta = new Map<number, { type: string; fill: string }>();
        if (Array.isArray(statuses)) {
            for (const s of statuses) {
                const idNum = typeof s?.id === 'string' ? Number(s.id) : s?.id;
                if (!Number.isFinite(idNum)) continue;
                const type = (s?.type ?? '').trim() || 'unknown';
                const fill = this.normalizeColor(s?.colorcode ?? undefined) ?? '#7b7b7b';
                statusIdToMeta.set(idNum as number, { type, fill });
            }
        }

        const typeToWorkareas = new Map<string, Workarea[]>();
        for (const w of visibleWorkareas) {
            const type = this.getWorkareaStatusType(w, statusIdToMeta);
            const arr = typeToWorkareas.get(type);
            if (arr) arr.push(w);
            else typeToWorkareas.set(type, [w]);
        }

        const simplifiedPolysByType: Array<{ type: string; polys: Feature<Polygon>[] }> = [];

        for (const [statusType, groupWorkareas] of typeToWorkareas.entries()) {
            const features: Feature<Point>[] = [];
            for (const w of groupWorkareas) {
                const f = this.convertWorkareaToFeature(w);
                if (f) features.push(f);
            }
            if (!features.length) continue;

            const clustered = clustersDbscan(featureCollection(features) as any, clusteringRadiusKm, {
                units: 'kilometers',
                minPoints: 1,
            });

            const clusters: Record<number, Feature<Point>[]> = {};
            for (const f of clustered.features) {
                const clusterId = f.properties?.cluster as number | undefined;
                if (clusterId === undefined) continue;
                (clusters[clusterId] ??= []).push(f);
            }

            const polys: Feature<Polygon>[] = [];
            for (const points of Object.values(clusters)) {
                if (points.length >= 3) {
                    const hull = convex(featureCollection(points));
                    if (hull) {
                        polys.push(buffer(hull, bufferKm, { units: 'kilometers' }) as Feature<Polygon>);
                    }
                } else {
                    for (const p of points) {
                        polys.push(buffer(p, pointBufferKm, { units: 'kilometers' }) as Feature<Polygon>);
                    }
                }
            }
            if (!polys.length) continue;

            const simplified = polys.map(
                p => simplify(p, { tolerance: simplifyTolerance, highQuality: false }) as Feature<Polygon>
            );

            simplifiedPolysByType.push({ type: statusType, polys: simplified });
        }

        const mergedByType: Array<{ type: string; polys: Feature<Polygon>[] }> = [];
        for (const entry of simplifiedPolysByType) {
            mergedByType.push({
                type: entry.type,
                polys: this.mergeIntersectingPolygons(entry.polys),
            });
        }

        const out: OutputPoly[] = [];
        for (const entry of mergedByType) {
            const groupWorkareas = typeToWorkareas.get(entry.type) ?? [];
            const fillColor = this.getTypeFillColor(entry.type, groupWorkareas, statuses, statusIdToMeta);

            const options: google.maps.PolygonOptions = {
                clickable: false,
                fillColor,
                fillOpacity: 0.25,
                strokeColor: fillColor,
                strokeOpacity: 0.8,
                strokeWeight: 1,
                zIndex: 1,
            };

            for (const poly of entry.polys) {
                out.push({
                    paths: this.convertPolygonToPath(poly),
                    color: fillColor,
                    options,
                });
            }
        }

        this.cacheSet(cacheKey, out);
        return out;
    }

    // ---- Stable cache key (sorted + quantized) ----

    private buildCacheKey(workareas: Workarea[], bucketZoom: number, statuses?: StatusLike[] | null): string {
        let h = 2166136261;

        const addNum = (n: number) => {
            h ^= n | 0;
            h = Math.imul(h, 16777619);
        };

        const addStr = (s: string) => {
            for (let i = 0; i < s.length; i++) {
                h ^= s.charCodeAt(i);
                h = Math.imul(h, 16777619);
            }
        };

        addNum(bucketZoom);
        addNum(workareas.length);

        const sortedWorkareas = [...workareas].sort((a: any, b: any) => (a?.id ?? 0) - (b?.id ?? 0));

        for (const w of sortedWorkareas) {
            const id = (w as any)?.id ?? 0;
            const sid = (w as any)?.status_id ?? 0;
            const lng = typeof (w as any)?.lng === 'string' ? parseFloat((w as any).lng) : (w as any)?.lng;
            const lat = typeof (w as any)?.lat === 'string' ? parseFloat((w as any).lat) : (w as any)?.lat;

            addNum(id);
            addNum(typeof sid === 'string' ? Number(sid) : (sid ?? 0));
            addNum(Number.isFinite(lng) ? Math.round(lng * 1e5) : 0);
            addNum(Number.isFinite(lat) ? Math.round(lat * 1e5) : 0);
        }

        if (Array.isArray(statuses)) {
            addNum(statuses.length);

            const sortedStatuses = [...statuses].sort((a: any, b: any) => {
                const ai = typeof a?.id === 'string' ? Number(a.id) : (a?.id ?? 0);
                const bi = typeof b?.id === 'string' ? Number(b.id) : (b?.id ?? 0);
                return ai - bi;
            });

            for (const s of sortedStatuses) {
                const idNum = typeof s?.id === 'string' ? Number(s.id) : (s?.id ?? 0);
                addNum(Number.isFinite(idNum) ? (idNum as number) : 0);
                addStr(String(s?.type ?? ''));
                addStr(String(s?.colorcode ?? ''));
            }
        } else {
            addStr('no-statuses');
        }

        return `bucket=${bucketZoom}|n=${workareas.length}|h=${h >>> 0}`;
    }

    private cacheSet(key: string, value: OutputPoly[]): void {
        if (this.cache.size >= this.cacheMaxEntries) {
            const firstKey = this.cache.keys().next().value as string | undefined;
            if (firstKey) {
                this.cache.delete(firstKey);
            }
        }

        this.cache.set(key, { value });
    }

    // ---- Logic helpers ----

    private getWorkareaStatusType(
        workarea: Workarea,
        statusIdToMeta: Map<number, { type: string; fill: string }>
    ): string {
        const sid = (workarea as any)?.status_id;
        const sidNum = typeof sid === 'string' ? Number(sid) : sid;
        const meta = Number.isFinite(sidNum) && sidNum !== undefined ? statusIdToMeta.get(sidNum as number) : undefined;
        return meta?.type ?? 'unknown';
    }

    private getTypeFillColor(
        statusType: string,
        groupWorkareas: Workarea[],
        statuses: StatusLike[] | null | undefined,
        statusIdToMeta: Map<number, { type: string; fill: string }>
    ): string {
        // Prefer: one status entry of that type
        if (Array.isArray(statuses)) {
            const s = statuses.find(x => (x?.type ?? '').trim() === statusType);
            const c = s ? this.normalizeColor(s.colorcode ?? undefined) : null;
            if (c) return c;
        }

        // Fallback: meta from workarea status ids
        for (const w of groupWorkareas) {
            const sid = (w as any)?.status_id;
            const sidNum = typeof sid === 'string' ? Number(sid) : sid;
            const meta =
                Number.isFinite(sidNum) && sidNum !== undefined ? statusIdToMeta.get(sidNum as number) : undefined;
            if (meta?.fill) return meta.fill;
        }

        return '#7b7b7b';
    }

    private mergeIntersectingPolygons(polys: Feature<Polygon>[]): Feature<Polygon>[] {
        if (polys.length <= 1) return polys;

        const parent = Array.from({ length: polys.length }, (_, i) => i);
        const find = (x: number): number => (parent[x] === x ? x : (parent[x] = find(parent[x])));
        const unite = (a: number, b: number) => {
            a = find(a);
            b = find(b);
            if (a !== b) parent[b] = a;
        };

        const bboxes = polys.map(p => bbox(p));
        const bboxOverlaps = (a: number[], b: number[]) => a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];

        for (let i = 0; i < polys.length; i++) {
            for (let j = i + 1; j < polys.length; j++) {
                if (!bboxOverlaps(bboxes[i], bboxes[j])) continue;
                if (booleanIntersects(polys[i], polys[j])) unite(i, j);
            }
        }

        const groups = new Map<number, number[]>();
        for (let i = 0; i < polys.length; i++) {
            const r = find(i);
            (groups.get(r) ?? groups.set(r, []).get(r)!).push(i);
        }

        const out: Feature<Polygon>[] = [];

        for (const idxs of groups.values()) {
            if (idxs.length === 1) {
                out.push(polys[idxs[0]]);
                continue;
            }

            const cleaned = idxs.map(i => cleanCoords(polys[i]) as Feature<Polygon>);
            let acc: Feature<Polygon> = cleaned[0];

            for (let k = 1; k < cleaned.length; k++) {
                const u = this.unionTwo(acc, cleaned[k]);
                if (u) acc = u;
                else {
                    out.push(acc);
                    acc = cleaned[k];
                }
            }

            if (acc.geometry.type === 'Polygon') out.push(acc);
            else if (acc.geometry.type === 'MultiPolygon') {
                out.push(...acc.geometry.coordinates.map((coords: any) => polygon(coords)));
            } else {
                out.push(...cleaned);
            }
        }

        return out;
    }

    private unionTwo(a: Feature<Polygon>, b: Feature<Polygon>): Feature<Polygon> | null {
        try {
            const u2: any = (union as any)(a, b);
            if (u2?.geometry) return u2 as Feature<Polygon>;
        } catch {}

        try {
            const fc = featureCollection([a, b]);
            const ufc: any = (union as any)(fc);
            if (ufc?.geometry) return ufc as Feature<Polygon>;
        } catch {}

        return null;
    }

    private convertWorkareaToFeature(workarea: Workarea): Feature<Point> | null {
        const lng =
            typeof (workarea as any)?.lng === 'string' ? parseFloat((workarea as any).lng) : (workarea as any)?.lng;

        const lat =
            typeof (workarea as any)?.lat === 'string' ? parseFloat((workarea as any).lat) : (workarea as any)?.lat;

        if (typeof lng === 'number' && typeof lat === 'number' && !Number.isNaN(lng) && !Number.isNaN(lat)) {
            return point([lng, lat], { id: (workarea as any).id });
        }

        return null;
    }

    private convertPolygonToPath(polygon: Feature<Polygon>): google.maps.LatLngLiteral[] {
        const outerRing = polygon.geometry.coordinates[0] ?? [];
        return outerRing.map(([lng, lat]) => ({ lat, lng }));
    }

    private normalizeColor(color?: string): string | null {
        if (!color) return null;
        const cleaned = String(color).trim();
        if (!cleaned) return null;
        return cleaned.startsWith('#') ? cleaned : `#${cleaned}`;
    }
}
